// ===== DATE UTILITY FUNCTIONS =====

// Date format regex patterns
export const SERVER_DATE_FORMAT_REGEX = /^\d{4}\/\d{2}\/\d{2}$/;
export const DISPLAY_DATE_FORMAT_REGEX = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

/**
 * Formats a Date object to MM/DD/YYYY string format for display
 * @param {Date} date - The date object to format
 * @returns {string} Date string in MM/DD/YYYY format
 */
export const formatDateToMMDDYYYY = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * Converts date string from MM/DD/YYYY to YYYY/MM/DD format for server communication
 * @param {string} dateString - Date in MM/DD/YYYY or YYYY/MM/DD format
 * @returns {string} Date string in YYYY/MM/DD format, empty string if invalid
 */
export const convertToServerFormat = (dateString: string): string => {
  if (!dateString) return '';

  // If already in YYYY/MM/DD format, return as is
  if (dateString.match(SERVER_DATE_FORMAT_REGEX)) {
    return dateString;
  }

  // Convert MM/DD/YYYY to YYYY/MM/DD
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

/**
 * Converts date string from YYYY/MM/DD to MM/DD/YYYY format for display
 * @param {string} dateString - Date in YYYY/MM/DD or MM/DD/YYYY format
 * @returns {string} Date string in MM/DD/YYYY format, empty string if invalid
 */
export const convertToDisplayFormat = (dateString: string): string => {
  if (!dateString) return '';

  // If already in MM/DD/YYYY format, return as is
  if (dateString.match(DISPLAY_DATE_FORMAT_REGEX)) {
    return dateString;
  }

  // Convert YYYY/MM/DD to MM/DD/YYYY
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return formatDateToMMDDYYYY(date);
};

/**
 * Checks if a date string represents an expired date (before today)
 * @param {string} dateString - Date string in any format
 * @returns {boolean} True if the date is expired (before today)
 */
export const isDateExpired = (dateString: string): boolean => {
  if (!dateString) return false;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;

  const today = new Date();

  // Normalize to beginning of day for accurate comparison
  date.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return date.getTime() < today.getTime();
};

/**
 * Adds specified number of days to the current date and returns formatted string
 * @param {number} daysToAdd - Number of days to add to current date
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
export const addDaysToCurrentDate = (daysToAdd: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  return formatDateToMMDDYYYY(date);
};
